/**
 * One version of an artifact, drawn: a page or an SVG in a frame with no
 * origin (artifactFrame.ts), a document as Markdown, a quiz as its cards.
 * The same in the chat's panel and on a shared link's page.
 *
 * A page or an SVG whose reply read something from outside (`unsafe`) does
 * not run on its own — it could send what it read anywhere its scripts reach.
 * The person clicks "Run this page" to run it, once per version (the frame is
 * keyed on the id and version, so another version waits for its own click).
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ARTIFACT_FRAME_PATH, type ArtifactKind } from '@wire';
import { ds } from '../ds';
import { deliver, documentFor, frameDocument, framed, readQuiz, type FramedKind } from '../artifactFrame';
import { QuizCards } from './ArtifactPanel';
import { Markdown } from './Markdown';
import { Ph } from './ui';

/** A page, an SVG or a component, in its own frame. Mounted once per
 *  version (its `key`): a frame is handed one document, once. A component
 *  is compiled first, and its frame made only once it has been. */
export function ArtifactFrame({
  kind,
  content,
  tokens,
  title
}: {
  kind: FramedKind;
  content: string;
  tokens: string | null;
  title: string;
}) {
  const [html, setHtml] = useState<string | null>(() => (kind === 'react' ? null : frameDocument(kind, content, tokens)));
  useEffect(() => {
    if (kind !== 'react') return;
    let gone = false;
    void documentFor(kind, content, tokens).then((doc) => !gone && setHtml(doc));
    return () => {
      gone = true;
    };
  }, [kind, content, tokens]);
  if (html === null) return <div className="pulse" aria-label="Loading" style={{ height: '100%', background: 'var(--app-panel-sunken)' }} />;
  return <Framed html={html} title={title} />;
}

function Framed({ html, title }: { html: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  // Before the frame can load and say it is ready.
  useLayoutEffect(() => deliver(window, () => ref.current?.contentWindow ?? null, html), [html]);
  return (
    <iframe
      ref={ref}
      src={ARTIFACT_FRAME_PATH}
      title={title}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      style={{ display: 'block', width: '100%', height: '100%', border: 0, background: '#fff' }}
    />
  );
}

/** A page or an SVG that runs only on a click, drawn until then as a prompt. */
function RunGate({ kind, content, tokens, title }: { kind: FramedKind; content: string; tokens: string | null; title: string }) {
  const [run, setRun] = useState(false);
  const { Button } = ds();
  if (run) return <ArtifactFrame kind={kind} content={content} tokens={tokens} title={title} />;
  return (
    <div style={{ height: '100%', display: 'grid', placeItems: 'center', padding: 24, boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 360, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
        <Ph name="shield-check" size={26} color="var(--text-muted)" />
        <span style={{ fontSize: 14, color: 'var(--text-body)' }}>This was built from content Assist read elsewhere.</span>
        <Button variant="accent" density="app" size="sm" iconLeft="play" onClick={() => setRun(true)}>
          Run this page
        </Button>
      </div>
    </div>
  );
}

export function ArtifactView({
  id,
  kind,
  content,
  tokens,
  title,
  version,
  unsafe
}: {
  /** The artifact's id, or any stable id for the one on a shared page: the
   *  frame is keyed on it and the version, so one frame never gets a second
   *  artifact. */
  id: string;
  kind: ArtifactKind;
  content: string;
  tokens: string | null;
  title: string;
  version: number;
  /** The reply read something from outside: a page or an SVG runs on a click. */
  unsafe: boolean;
}) {
  if (framed(kind)) {
    const props = { kind, content, tokens, title };
    return unsafe ? <RunGate key={`${id}:${version}`} {...props} /> : <ArtifactFrame key={`${id}:${version}`} {...props} />;
  }
  const quiz = kind === 'quiz' ? readQuiz(content) : null;
  return (
    <div
      className="scroll selectable"
      style={{ height: '100%', overflow: 'auto', padding: 16, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {quiz ? <QuizCards questions={quiz} /> : <Markdown text={content} />}
    </div>
  );
}
